import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { RefreshCw, CreditCard } from 'lucide-react'
import { ServerApiClient, type FounderBillingResponse } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

type Props = { topInsetPx?: number }

const panel: CSSProperties = {
  border: '1px solid #d9e2df',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  background: '#fff',
  color: '#15343b',
  borderRadius: 7,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}
const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: '#5d6d72',
  fontWeight: 800,
  borderBottom: '1px solid #e4ece9',
  padding: '8px 10px',
  whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12,
  color: '#223236',
  borderBottom: '1px solid #eef3f1',
  padding: '8px 10px',
  verticalAlign: 'top',
}
const numTd: CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const fmtMoney = (cents: number | null | undefined): string => {
  if (cents == null || !Number.isFinite(Number(cents))) return '-'
  return `$${(Number(cents) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}
const fmtNum = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(Number(v)) ? '-' : Number(v).toLocaleString()

function MetricCard({ label, value, sub, accent }: { label: string; value: string; sub?: string; accent?: string }) {
  return (
    <div style={{ ...panel, padding: '10px 12px', display: 'grid', gap: 2 }}>
      <div style={{ fontSize: 11, color: '#5d6d72', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 850, color: accent || '#15343b', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: '#7b898d' }}>{sub}</div> : null}
    </div>
  )
}

/** Founder Billing tab: MRR, subscription/trial counts, trials lapsing soon, plan breakdown, recent webhook activity. */
export function FounderBillingDashboard({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const loadSeqRef = useRef(0)
  const didInitialLoadRef = useRef(false)
  const [data, setData] = useState<FounderBillingResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    const requestId = loadSeqRef.current + 1
    loadSeqRef.current = requestId
    setLoading(true)
    setError('')
    try {
      const session = await ensureApiSessionReady()
      if (requestId !== loadSeqRef.current) return
      if (!session.token) {
        const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
        window.history.replaceState(window.history.state, '', `/login?next=${encodeURIComponent(next)}`)
        window.dispatchEvent(new Event('fg_route_change'))
        return
      }
      const out = await api.getFounderBilling()
      if (requestId !== loadSeqRef.current) return
      setData(out)
    } catch (e) {
      if (requestId !== loadSeqRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load billing')
    } finally {
      if (requestId === loadSeqRef.current) setLoading(false)
    }
  }, [api])

  useEffect(() => {
    if (didInitialLoadRef.current) return
    didInitialLoadRef.current = true
    void load()
  }, [load])

  return (
    <div style={{ padding: 16, paddingTop: topInsetPx + 16, display: 'grid', gap: 14, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <CreditCard size={16} color="#15343b" />
        <strong style={{ fontSize: 14, color: '#15343b' }}>Billing</strong>
        {data ? <span style={{ fontSize: 11, color: '#7b898d' }}>as of {fmtDate(data.generated_at)}</span> : null}
        <span style={{ flex: 1 }} />
        <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
          <RefreshCw size={13} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {error ? (
        <div style={{ ...panel, padding: 12, borderColor: '#e5b4b4', background: '#fdeeee', color: '#8c2f2f', fontSize: 12 }}>{error}</div>
      ) : null}
      {data && !data.stripe_configured ? (
        <div style={{ ...panel, padding: 10, borderColor: '#e0d3ae', background: '#faf5e4', color: '#6d5a1d', fontSize: 11 }}>
          Stripe keys are not configured on this server — counts reflect the DB only; checkout/portal are disabled.
        </div>
      ) : null}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 8 }}>
        <MetricCard label="MRR (committed)" value={data ? `${fmtMoney(data.mrr_cents)}/mo` : '—'} sub="active + past due" accent="#1e5c3a" />
        <MetricCard label="Trial pipeline" value={data ? `${fmtMoney(data.trial_pipeline_cents)}/mo` : '—'} sub="if trials convert" />
        <MetricCard label="Active subs" value={data ? fmtNum(data.active_subscriptions) : '—'} />
        <MetricCard label="In trial" value={data ? fmtNum(data.trialing_subscriptions + data.app_trials) : '—'} sub={data ? `${fmtNum(data.trialing_subscriptions)} carded · ${fmtNum(data.app_trials)} no card` : undefined} />
        <MetricCard label="Past due" value={data ? fmtNum(data.past_due) : '—'} accent={data && data.past_due > 0 ? '#8c2f2f' : undefined} />
        <MetricCard label="Canceled" value={data ? fmtNum(data.canceled) : '—'} />
        <MetricCard label="Partner clients" value={data ? fmtNum(data.partner_client_units) : '—'} sub="provisioned billable units" />
        <MetricCard label="Partner drift" value={data ? fmtNum(data.partner_quantity_drift) : '—'} sub={data ? `${fmtNum(data.partner_sync_failures)} sync failures` : undefined} accent={data && (data.partner_quantity_drift > 0 || data.partner_sync_failures > 0) ? '#8c2f2f' : undefined} />
        <MetricCard label="Tenants" value={data ? fmtNum(data.total_tenants) : '—'} sub={data ? `${fmtNum(data.legacy_tenants)} legacy (ungated)` : undefined} />
      </div>

      {/* Trials expiring soon */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>
          Trials ending within 7 days {data ? `(${data.trials_expiring_soon.length})` : ''}
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Company</th>
              <th style={thStyle}>Slug</th>
              <th style={thStyle}>Chose</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Ends</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Days left</th>
            </tr>
          </thead>
          <tbody>
            {(data?.trials_expiring_soon || []).map((t) => (
              <tr key={t.slug}>
                <td style={tdStyle}>{t.tenant_name || '(unnamed)'}</td>
                <td style={tdStyle}>{t.slug}</td>
                <td style={tdStyle}>{t.plan_code}</td>
                <td style={numTd}>{fmtDate(t.trial_ends_at)}</td>
                <td style={numTd}>{fmtNum(t.days_left)}</td>
              </tr>
            ))}
            {data && !data.trials_expiring_soon.length ? (
              <tr><td style={{ ...tdStyle, color: '#7b898d' }} colSpan={5}>No trials ending in the next 7 days.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {/* Plan breakdown */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>By plan &amp; status</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Plan</th>
              <th style={thStyle}>Interval</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Tenants</th>
            </tr>
          </thead>
          <tbody>
            {(data?.by_plan || []).map((r, i) => (
              <tr key={`${r.plan_code}-${r.plan_interval}-${r.plan_status}-${i}`}>
                <td style={tdStyle}>{r.plan_code}</td>
                <td style={tdStyle}>{r.plan_interval}</td>
                <td style={tdStyle}>{r.plan_status}</td>
                <td style={numTd}>{fmtNum(r.count)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Recent webhook activity */}
      <div style={{ ...panel, overflow: 'auto' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>Recent Stripe events</div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Event</th>
              <th style={thStyle}>Tenant</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>When</th>
            </tr>
          </thead>
          <tbody>
            {(data?.recent_events || []).map((e, i) => (
              <tr key={i}>
                <td style={tdStyle}>{e.event_type}</td>
                <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 11 }}>{e.tenant_id ? e.tenant_id.slice(0, 8) : '—'}</td>
                <td style={numTd}>{fmtDate(e.created_at)}</td>
              </tr>
            ))}
            {data && !data.recent_events.length ? (
              <tr><td style={{ ...tdStyle, color: '#7b898d' }} colSpan={3}>No billing events yet.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )
}
